/**
 * Presence-Store (Jörg 2026-09-11: „ich möchte den Status auf der
 * Geschäftsführungsseite bei Mannschaft sehen") — UI-Unterbau zur
 * PWA-Offline-P1.4-Presence. Singleton nach dem Muster des Badge-Streams:
 * solange mindestens ein Subscriber da ist, wird GET /portal/presence alle
 * 60s gepollt. Opt-outs sind serverseitig gefiltert, kein GPS
 * ([[feedback_datenschutz_prinzip]]). Presence lebt serverseitig in-memory —
 * nach Portal-Neustart ist die Liste leer, bis sich Nutzer verbinden.
 */
import { useEffect, useState } from 'react';
import { api } from '@onvice/portal-shell-api';

export interface PresenceUser {
  user_id: string;
  display_name: string;
  online: boolean;
  last_seen_at: string;
}

export interface PresenceState {
  /** Tenant-Schalter presence_enabled — bei false zeigt die UI nichts an. */
  enabled: boolean;
  /** userId → Presence-Eintrag. Fehlt ein User: seit Portal-Start nie verbunden. */
  users: Map<string, PresenceUser>;
  /** true sobald die erste Antwort da ist (vorher nichts rendern, kein Flackern). */
  loaded: boolean;
}

const EMPTY: PresenceState = { enabled: true, users: new Map(), loaded: false };

export function seitLabel(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 1) return 'gerade eben';
  if (min < 60) return `seit ${min} Min.`;
  const h = Math.floor(min / 60);
  if (h < 24) return `seit ${h} Std.`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'seit 1 Tag' : `seit ${d} Tagen`;
}

type Listener = (s: PresenceState) => void;

class PresenceStore {
  private state: PresenceState = EMPTY;
  private listeners = new Set<Listener>();
  private timer: ReturnType<typeof setInterval> | null = null;

  private async load(): Promise<void> {
    try {
      const r = await api.get<{ success: boolean; data: { enabled: boolean; users: PresenceUser[] } }>('/portal/presence');
      const users = new Map<string, PresenceUser>();
      for (const u of r.data.users ?? []) users.set(u.user_id, u);
      this.state = { enabled: r.data.enabled, users, loaded: true };
      for (const l of this.listeners) l(this.state);
    } catch { /* alter Stand bleibt stehen — kein Flackern bei Netz-Wacklern */ }
  }

  subscribe(cb: Listener): () => void {
    this.listeners.add(cb);
    cb(this.state);
    if (this.listeners.size === 1) {
      void this.load();
      this.timer = setInterval(() => void this.load(), 60_000);
    }
    return () => {
      this.listeners.delete(cb);
      if (this.listeners.size === 0 && this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    };
  }
}

const store = new PresenceStore();

export function usePresence(): PresenceState {
  const [state, setState] = useState<PresenceState>(EMPTY);
  useEffect(() => store.subscribe(setState), []);
  return state;
}

/**
 * Dezenter Status-Punkt hinter einem Personen-Namen (zwei Zustände,
 * Jörg-Entscheid): ● grün = Portal gerade offen, ○ hohl = offline seit X.
 * Rendert NICHTS, wenn Presence deaktiviert ist, noch lädt — oder der User
 * nicht in der Liste steht (Bob-Fund 2026-09-11: nicht gelistet heißt
 * Opt-out ODER seit Portal-Neustart nie verbunden; ein hohler
 * „offline"-Punkt wäre bei Opt-out irreführend und verletzt „Nicht
 * anzeigen").
 */
export function PresenceDot({ userId }: { userId: string }) {
  const presence = usePresence();
  if (!presence.enabled || !presence.loaded) return null;
  const p = presence.users.get(userId);
  if (!p) return null;
  const title = p.online ? 'online' : `offline ${seitLabel(p.last_seen_at)}`;
  return (
    <span
      title={title}
      aria-label={title}
      className={`inline-block w-2 h-2 rounded-full ml-1.5 align-middle shrink-0 ${p.online ? 'bg-emerald-500' : 'border border-muted-foreground/50'}`}
    />
  );
}
